import { RefObject } from 'react';
import { Tabbable } from 'reakit';
import { Box } from '../../../Box/Box';
import { focus } from '../../../utils/focusable';
import { CodeTypes, FileStates } from '../../common';
import { FILE_ERRORS } from '../../helpers';
import { FileType } from '../../types';
import { CancelUploadButton } from './inner/CancelUploadButton';
import { DeleteFile } from './inner/DeleteFile';
import { FileBlock } from './inner/FileBlock';
import { LoadingIndicator } from './inner/LoadingIndicator';
import { TryAgainButton } from './inner/TryAgainButton';

type FilesFormProps = {
  inputRef: RefObject<HTMLInputElement>;
  files: FileType[];
  onTryAgain: (id: string) => void;
  onDeleteFile: (id: string) => void;
  onCancel: (id: string) => void;
};

export function FilesForm({
  inputRef,
  files,
  onTryAgain,
  onDeleteFile,
  onCancel,
}: FilesFormProps) {
  if (!files.length) return null;
  return (
    <Box mb="sp-16" bg="white" width={1}>
      {files.map(({ id, code, state, file }, index) => {
        const isExternalError =
          code !== CodeTypes.SIZE_EXCEEDED &&
          code !== CodeTypes.VALIDATION_FAILED;
        const isFileWithErrors =
          code !== CodeTypes.SUCCESS && code !== CodeTypes.LOADING;
        const isTryAgainButtonVisible =
          code.length && isFileWithErrors && isExternalError;
        const errorMessage = Object.keys(FILE_ERRORS).includes(code)
          ? FILE_ERRORS[code as keyof typeof FILE_ERRORS]
          : '';
        let ariaLabelContent = `Файл загружен ${file.name}`;
        if (errorMessage) {
          ariaLabelContent = `${errorMessage}. ${file.name}`;
        } else if (code === CodeTypes.LOADING) {
          ariaLabelContent = `Загрузка ${file.name}`;
        }
        const setFocusToNextElement = () => {
          const nextElementId = files[index + 1]?.id;
          const focusedElement = document.getElementById(nextElementId);
          if (focusedElement) {
            focusedElement.focus();
          } else {
            inputRef.current?.focus();
          }
        };
        const handleCancel = () => {
          onCancel(id);
          setFocusToNextElement();
        };
        const handleDeleteFile = () => {
          onDeleteFile(id);
          setFocusToNextElement();
        };

        return (
          <Tabbable
            as={Box}
            key={id}
            id={id}
            pt="fileCardPaddingTop"
            pr="fileCardPaddingRight"
            pb="fileCardPaddingBottom"
            pl="fileCardPaddingLeft"
            border="1"
            borderColor="fileCardBorderColor"
            borderRadius="fileCard"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt="sp-8"
            styles={{
              ':focus': focus,
              ':first-child': {
                mt: 0,
              },
              label: 'FileCard',
            }}
          >
            {state !== FileStates.loading && (
              <>
                <Box
                  styles={{ label: 'FileName' }}
                  aria-label={ariaLabelContent}
                  pt="sp-8"
                  pb={isTryAgainButtonVisible ? 'sp-0' : 'sp-8'}
                  pr="sp-16"
                >
                  <FileBlock fileName={file.name} errorMessage={errorMessage} />
                  {isTryAgainButtonVisible && (
                    <Box mt="sp-12" ml="sp-32">
                      <TryAgainButton
                        fileName={file.name}
                        onTryAgainClick={() => onTryAgain(id)}
                      />
                    </Box>
                  )}
                </Box>
                <DeleteFile
                  fileName={file.name}
                  onDeleteClick={handleDeleteFile}
                />
              </>
            )}
            {state === FileStates.loading && (
              <>
                <Box
                  aria-label={ariaLabelContent}
                  color="fileUploaderLoadingIndicatorColor"
                  position="relative"
                  display="flex"
                  alignItems="flex-start"
                  py="sp-8"
                  pr="sp-16"
                >
                  <LoadingIndicator />
                  <Box
                    ml="sp-8"
                    color="gray-90"
                    styles={{ wordBreak: 'break-word' }}
                  >
                    {file.name}
                  </Box>
                </Box>
                <CancelUploadButton
                  fileName={file.name}
                  onCancel={handleCancel}
                />
              </>
            )}
          </Tabbable>
        );
      })}
    </Box>
  );
}
